import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import { useAdminAnalytics } from '../../hooks/useAdminAnalytics';
import EventStreamPanel from '../../components/admin/analytics/EventStreamPanel';
import RevenueStreamPanel from '../../components/admin/analytics/RevenueStreamPanel';
import ComplianceAuditPanel from '../../components/admin/analytics/ComplianceAuditPanel';

const AdminAnalytics = () => {
  // Use the custom hook for analytics data
  const {
    analytics,
    isLoading,
    error,
    period,
    setPeriod,
    periodOptions,
    refetch
  } = useAdminAnalytics('month');

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <p className="text-red-600">Failed to load analytics. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-6 h-6" />
                Analytics
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Comprehensive platform analytics and insights
              </p>
              <Link
                to="/admin/advanced-analytics"
                className="mt-2 inline-block text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                View Advanced Analytics →
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <SearchSelect
                value={period}
                onChange={setPeriod}
                options={periodOptions}
                className="w-40"
              />
              <button
                onClick={() => refetch()}
                className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white border rounded-lg p-6">
            <div className="text-center">Loading analytics...</div>
          </div>
        ) : (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Revenue</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      ${(analytics.overview?.total_revenue || 0).toFixed(2)}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-green-500" />
                </div>
              </div>
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Bookings</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {analytics.overview?.total_bookings || 0}
                    </p>
                  </div>
                  <Calendar className="w-8 h-8 text-blue-500" />
                </div>
              </div>
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Platform Fees</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      ${(analytics.overview?.platform_fees || 0).toFixed(2)}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-purple-500" />
                </div>
              </div>
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Completed Bookings</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {analytics.overview?.completed_bookings || 0}
                    </p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-indigo-500" />
                </div>
              </div>
            </div>

            {/* Detailed Analytics Sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Bookings Analytics */}
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Bookings Analytics</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Total Bookings</span>
                    <span className="text-sm font-medium">{analytics.bookings_analytics?.total || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Average Value</span>
                    <span className="text-sm font-medium">
                      ${(analytics.bookings_analytics?.average_value || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Conversion Rate</span>
                    <span className="text-sm font-medium">
                      {(analytics.bookings_analytics?.conversion_rate || 0).toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Revenue Analytics */}
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Revenue Analytics</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Total Revenue</span>
                    <span className="text-sm font-medium">
                      ${(analytics.revenue_analytics?.total_revenue || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Platform Fees</span>
                    <span className="text-sm font-medium">
                      ${(analytics.revenue_analytics?.platform_fees || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Average Transaction</span>
                    <span className="text-sm font-medium">
                      ${(analytics.revenue_analytics?.average_transaction || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <EventStreamPanel eventStream={analytics?.event_stream} />
            <RevenueStreamPanel revenueStream={analytics?.revenue_stream} />
            <ComplianceAuditPanel complianceAudit={analytics?.compliance_audit} />

            {/* Top Performers */}
            {analytics.top_performers && (
              <div className="bg-white border rounded-lg p-3 sm:p-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Top Performers</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Top Vendors</h3>
                    <div className="space-y-2">
                      {analytics.top_performers.top_vendors?.slice(0, 5).map((vendor, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-600">{vendor.business_name}</span>
                          <span className="font-medium">${parseFloat(vendor.revenue || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Top Services</h3>
                    <div className="space-y-2">
                      {analytics.top_performers.top_services?.slice(0, 5).map((service, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-600">{service.service_name}</span>
                          <span className="font-medium">${parseFloat(service.revenue || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;

